import { ArrowUpRight, Clock, Linkedin, Mail, Phone, Sparkles, Star, Store, Users, Zap } from "lucide-react";
import { IEstablishment } from "../../../interfaces/establishment";
import { FounderCounts, founderCountLabel } from "../../../lib/founderCounts";
import { Badge } from "../../../components/ui/Badge";
import { Avatar } from "../../linkedin/parts";
import {
    callWindow, DAY_ORDER, dayLabel, fmtDate, fmtEuros, HistoryItem, Person, relativeFr, revenue, WhyNowItem,
} from "./model";
import { ContactLine, Field, Section, TONE_DOT } from "./parts";

/* ─── Pourquoi maintenant ───────────────────────────────────────────────── */

export function WhyNow({ items, lastEvent, onOpen }: { items: WhyNowItem[]; lastEvent?: HistoryItem; onOpen: (historyId: string) => void }) {
    return (
        <Section title="Pourquoi maintenant" icon={Zap}>
            {items.length === 0 ? (
                <p className="font-sans text-sm text-ink-muted">
                    Aucun signal récent.
                    {lastEvent && <> Dernier événement : {lastEvent.title.toLowerCase()}, {relativeFr(lastEvent.date)}.</>}
                </p>
            ) : (
                <ul className="divide-y divide-border">
                    {items.map((it) => (
                        <li key={it.id}>
                            <button
                                type="button"
                                disabled={!it.historyId}
                                onClick={() => it.historyId && onOpen(it.historyId)}
                                className="flex w-full items-start gap-3 rounded-lg py-3 text-left transition-colors duration-150 first:pt-0 enabled:hover:bg-surface-sunken/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                            >
                                <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${TONE_DOT[it.tone]}`} />
                                <span className="min-w-0 flex-1">
                                    <span className="block font-sans text-sm font-medium text-ink">{it.fact}</span>
                                    <span className="mt-0.5 block font-sans text-sm text-ink-muted">→ {it.angle}</span>
                                </span>
                                <span className="shrink-0 font-sans text-xs text-ink-faint">{relativeFr(it.date)}</span>
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </Section>
    );
}

/* ─── Décideurs ─────────────────────────────────────────────────────────── */

export function DecisionMakers({ people, counts, closed }: { people: Person[]; counts: Record<string, FounderCounts>; closed: boolean }) {
    return (
        <Section id="decideurs" title="Décideurs" icon={Users} action={people.length > 0 && <span className="font-sans text-sm text-ink-muted">{people.length}</span>}>
            {people.length === 0 ? (
                <p className="font-sans text-sm text-ink-muted">Aucun dirigeant connu pour cet établissement. Le standard reste le meilleur point d'entrée.</p>
            ) : (
                <ul className="divide-y divide-border">
                    {people.map((p) => {
                        const portfolio = founderCountLabel(counts[p.id]);
                        const many = (counts[p.id]?.active ?? 0) > 1;
                        const founderHref = `/signals/founder?founderName=${encodeURIComponent(p.name)}`;
                        return (
                            <li key={p.id} className="flex flex-col gap-3 py-4 first:pt-0 last:pb-0 md:flex-row md:items-start">
                                <div className="flex min-w-0 flex-1 items-start gap-3">
                                    <Avatar name={p.name || "?"} />
                                    <div className="min-w-0">
                                        {p.name ? (
                                            <a href={founderHref} className="font-sans text-sm font-semibold text-ink hover:underline">{p.name}</a>
                                        ) : (
                                            <p className="font-sans text-sm font-semibold text-ink">Nom non renseigné</p>
                                        )}
                                        <p className="font-sans text-sm text-ink-muted">
                                            {[p.mandate, p.age !== null ? `${p.age} ans` : null].filter(Boolean).join(" · ")}
                                        </p>
                                        {portfolio && p.name && (
                                            <a href={founderHref} className="mt-1 inline-flex items-center gap-1 font-sans text-xs font-medium text-ink-muted hover:text-ink">
                                                {portfolio}{many && " - voir le portefeuille"}
                                                <ArrowUpRight className="h-3 w-3" />
                                            </a>
                                        )}
                                    </div>
                                </div>
                                <div className="w-full space-y-1 md:w-72">
                                    {p.phone && <ContactLine icon={Phone} href={`tel:${p.phone}`} value={p.phone} />}
                                    {p.email && <ContactLine icon={Mail} href={`mailto:${p.email}`} value={p.email} />}
                                    {p.linkedinUrl && (
                                        <ContactLine icon={Linkedin} href={p.linkedinUrl} value="Profil LinkedIn" copy={false} external />
                                    )}
                                    {!p.phone && !p.email && !p.linkedinUrl && (
                                        <p className="font-sans text-sm text-ink-faint">{closed ? "-" : "Pas de coordonnées directes"}</p>
                                    )}
                                </div>
                            </li>
                        );
                    })}
                </ul>
            )}
        </Section>
    );
}

/* ─── Profil commercial ─────────────────────────────────────────────────── */

const flag = (v?: boolean) => (v === true ? "Oui" : v === false ? "Non" : null);

const TERRACE_SOURCE_LABELS: Record<string, string> = {
    paris_permits: "permis Ville de Paris",
    toulouse_permits: "permis Ville de Toulouse",
    strasbourg_permits: "permis Ville de Strasbourg",
    anglet_permits: "permis Ville d'Anglet",
    larochelle_permits: "permis Ville de La Rochelle",
    google: "Google",
    osm: "© les contributeurs OpenStreetMap",
    datatourisme: "DATAtourisme",
    manual: "saisie manuelle",
};

export function CommercialProfile({ establishment: e }: { establishment: IEstablishment }) {
    const ops = e.operations;
    const call = callWindow(e);
    const ca = revenue(e);
    const t = e.technologies;
    const stack = [
        { label: "Caisse", items: t?.pos_systems },
        { label: "Réservation", items: t?.online_booking_tools },
        { label: "Commande", items: t?.order_systems },
        { label: "Fidélité", items: t?.loyalty_systems },
        { label: "Paiement", items: t?.payment_methods },
        { label: "Achats", items: t?.purchase_solutions },
        { label: "RH", items: t?.hr_solutions },
        { label: "Qualité", items: t?.quality_systems },
        { label: "Site web", items: t?.website_cms },
    ].filter((g) => g.items && g.items.length > 0);
    const platforms = [
        e.channels?.uber_eats?.enabled && "Uber Eats",
        e.channels?.deliveroo?.enabled && "Deliveroo",
        e.channels?.just_eat?.enabled && "Just Eat",
    ].filter(Boolean) as string[];
    const terraceSource = ops?.terrace_source ? TERRACE_SOURCE_LABELS[ops.terrace_source] || ops.terrace_source : null;
    const hours = [...(ops?.opening_hours || [])].sort((a, b) => DAY_ORDER.indexOf(a.day.toLowerCase()) - DAY_ORDER.indexOf(b.day.toLowerCase()));
    const tags = [...new Set([...(e.cuisine_types || []), ...(e.categories || []), ...(e.subCategories || []), ...(e.tags || [])])];

    return (
        <Section id="profil" title="Profil commercial" icon={Store}>
            <div className="mb-5 flex items-start gap-3 rounded-lg bg-surface-sunken px-4 py-3">
                <Clock className="mt-0.5 h-4 w-4 shrink-0 text-ink-muted" />
                <div className="font-sans text-sm">
                    <p className="text-ink">
                        <span className="font-medium">Créneau d'appel conseillé : {call.window}</span>
                        <span className="text-ink-muted"> - hors service{call.fromHours ? "" : ", horaires inconnus"}</span>
                    </p>
                    {call.closedDays.length > 0 && (
                        <p className="mt-0.5 text-ink-muted">Fermé le {call.closedDays.join(", ")}.</p>
                    )}
                </div>
            </div>

            <div className="grid grid-cols-2 gap-x-6 gap-y-4 md:grid-cols-4">
                <Field label="Places assises">{ops?.seats_inside}</Field>
                <Field label="Places terrasse">{ops?.seats_terrace}</Field>
                <Field label="Terrasse">
                    {flag(ops?.has_terrace) && (
                        <>
                            {flag(ops?.has_terrace)}
                            {terraceSource && <span className="block font-sans text-xs text-ink-faint">{terraceSource}</span>}
                        </>
                    )}
                </Field>
                <Field label="Service à table">{flag(ops?.has_table_service)}</Field>
                <Field label="À emporter">{flag(ops?.has_takeaway)}</Field>
                <Field label="Livraison">
                    {platforms.length > 0 ? platforms.join(", ") : flag(ops?.has_delivery)}
                </Field>
                <Field label="Gamme de prix">{e.price_range}</Field>
                <Field label={ca ? `CA ${ca.year}` : "CA"}>
                    {ca && (
                        <>
                            {fmtEuros(ca.amount)}
                            {ca.delta !== null && (
                                <span className={`ml-1.5 font-sans text-xs font-medium ${ca.delta >= 0 ? "text-success" : "text-danger"}`}>
                                    {ca.delta > 0 ? "+" : ""}{ca.delta} % vs {ca.prevYear}
                                </span>
                            )}
                        </>
                    )}
                </Field>
                <Field label="Effectif">
                    {e.workforce?.employee_count ?? (e.registration?.workforce_range
                        ? `${e.registration.workforce_range} (société${e.registration.workforce_year ? `, ${e.registration.workforce_year}` : ""})`
                        : null)}
                </Field>
            </div>

            {stack.length > 0 && (
                <div className="mt-5 border-t border-border pt-5">
                    <p className="mb-3 font-sans text-xs font-semibold uppercase tracking-wide text-ink-faint">Outils en place</p>
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                        {stack.map((g) => (
                            <div key={g.label} className="flex items-start gap-3">
                                <span className="w-24 shrink-0 font-sans text-sm text-ink-muted">{g.label}</span>
                                <div className="flex flex-wrap gap-1.5">
                                    {g.items!.map((tool) => <Badge key={tool}>{tool}</Badge>)}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {hours.length > 0 && (
                <div className="mt-5 border-t border-border pt-5">
                    <p className="mb-3 font-sans text-xs font-semibold uppercase tracking-wide text-ink-faint">Horaires</p>
                    <div className="grid grid-cols-1 gap-x-8 gap-y-1 md:grid-cols-2">
                        {hours.map((h) => {
                            const open = !h.closed && h.open && h.close;
                            return (
                                <div key={h.day} className="flex justify-between font-sans text-sm">
                                    <span className="text-ink-muted">{dayLabel(h.day)}</span>
                                    <span className={open ? "text-ink" : "text-ink-faint"}>{open ? `${h.open} – ${h.close}` : "Fermé"}</span>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {(tags.length > 0 || e.description) && (
                <div className="mt-5 border-t border-border pt-5">
                    <p className="mb-3 font-sans text-xs font-semibold uppercase tracking-wide text-ink-faint">Activité</p>
                    {tags.length > 0 && (
                        <div className="flex flex-wrap gap-1.5">
                            {tags.map((tag) => <Badge key={tag}>{tag}</Badge>)}
                        </div>
                    )}
                    {e.description && (
                        <p className="mt-3 whitespace-pre-line font-sans text-sm leading-relaxed text-ink-muted">{e.description}</p>
                    )}
                </div>
            )}
        </Section>
    );
}

/* ─── Avis ──────────────────────────────────────────────────────────────── */

export function Reviews({ establishment: e }: { establishment: IEstablishment }) {
    const sources = (e.ratings?.source_summary || []).filter((s) => s.rating_avg);
    const sample = (e.ratings?.reviews_sample || []).filter((r) => r.text).slice(0, 3);
    if (sources.length === 0 && sample.length === 0) return null;
    return (
        <Section id="avis" title="Avis clients" icon={Star}>
            <div className="flex flex-wrap gap-6">
                {sources.map((s) => (
                    <div key={s.source}>
                        <p className="font-sans text-xs capitalize text-ink-muted">{s.source}</p>
                        <p className="mt-0.5 flex items-baseline gap-1.5 font-sans">
                            <span className="text-2xl font-semibold tracking-tight text-ink">{s.rating_avg!.toFixed(1).replace(".", ",")}</span>
                            <span className="text-sm text-ink-muted">/ 5 · {(s.rating_count || 0).toLocaleString("fr-FR")} avis</span>
                        </p>
                        {s.url && (
                            <a href={s.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 font-sans text-xs font-medium text-ink-muted hover:text-ink">
                                Lire les avis <ArrowUpRight className="h-3 w-3" />
                            </a>
                        )}
                    </div>
                ))}
            </div>
            {sample.length > 0 && (
                <ul className="mt-5 space-y-3 border-t border-border pt-5">
                    {sample.map((r, i) => (
                        <li key={r.review_id || i} className="font-sans text-sm">
                            <p className="line-clamp-3 leading-relaxed text-ink">« {r.text} »</p>
                            <p className="mt-1 text-xs text-ink-faint">
                                {[r.rating !== undefined ? `${r.rating}/5` : null, r.author_name, fmtDate(r.date), r.source].filter(Boolean).join(" · ")}
                            </p>
                            {r.aspects?.summary && (
                                <p className="mt-1 flex items-start gap-1.5 text-xs text-ink-muted">
                                    <Sparkles className="mt-0.5 h-3 w-3 shrink-0" />
                                    {r.aspects.summary}
                                </p>
                            )}
                        </li>
                    ))}
                </ul>
            )}
        </Section>
    );
}
